import React, { useState, useEffect, useCallback, useMemo } from "react";
import { connect } from "react-redux";
import { useQuery } from "@apollo/client";
import _ from "lodash";

import { getMultiCourseUserMapQuery } from "ClassRoom/modules/ClassRoomQuery";

import { setToastMsg } from "Login/modules/LoginModule/LoginActions";

import { getUpdatedArchivedFilters } from "Utils";
import { getDefaultOrderBy } from "Utils/GetFormattedUserName";
import { ASC } from "Constants/stringConstants";
import { getSelectedAcademicYearId } from "modules/CommonSelectors";

import AssignClassSettingsDataGrid from "./AssignClassSettingsDataGrid";

const AssignClassSettingsDataGridWrapper = props => {
  const {
    courses,
    assignmentId,
    assignmentTags,
    academicYearId,
    portalType,
    setToastMsg,
    ...otherProps
  } = props;

  // State for managing course user data and loading states
  const [coursesUserMap, setCoursesUserMap] = useState({});

  const courseIds = useMemo(() => _.map(courses, "id"), [courses]);

  // Only fetch data for courses that we don't already have
  const courseIdsToFetch = useMemo(() => {
    return _.filter(courseIds, courseId => !_.get(coursesUserMap, courseId));
  }, [courseIds, coursesUserMap]);

  // Create course user map filters similar to AddStudentsDropdown
  const courseUserMapFilters = useMemo(() => {
    return {
      ...(getUpdatedArchivedFilters(
        {},
        {
          entityType: "ASSIGNMENT",
          entityId: assignmentId,
        }
      ) || {}),
      tags: assignmentTags,
      userTypes: ["student"],
      orderBy: getDefaultOrderBy({ type: "student" }),
      orderByDirection: ASC,
      academicYearIds: academicYearId ? [academicYearId] : undefined,
      archivalState: undefined,
    };
  }, [assignmentId, assignmentTags, academicYearId]);

  // Fetch course user map data for the newly added courses
  const { data, error } = useQuery(getMultiCourseUserMapQuery, {
    variables: {
      ids: courseIdsToFetch,
      portalType,
      filters: courseUserMapFilters,
      userArchivedFilters: {
        academicYearId,
      },
    },
    fetchPolicy: "network-only",
    skip: _.isEmpty(courseIdsToFetch),
  });

  // Function to update courses user map with new data
  const updateCoursesUserMap = useCallback(data => {
    const multiNodeData = _.get(data, "multiNode");
    if (multiNodeData) {
      const newCoursesUserMap = _.reduce(
        multiNodeData,
        (acc, courseData) => {
          const courseId = _.get(courseData, "id");
          if (courseData && courseId) {
            acc[courseId] = courseData;
          }
          return acc;
        },
        {}
      );

      setCoursesUserMap(prev => ({ ...prev, ...newCoursesUserMap }));
    }
  }, []);

  // Update course user map when data is received
  useEffect(() => {
    updateCoursesUserMap(data);
  }, [data, updateCoursesUserMap]);

  const handleQueryError = useCallback(
    error => {
      if (error) {
        setToastMsg("toastMsgs:something_went_wrong");
      }
    },
    [setToastMsg]
  );

  // Handle errors
  useEffect(() => {
    handleQueryError(error);
  }, [error, handleQueryError]);

  return (
    <AssignClassSettingsDataGrid
      {...otherProps}
      courses={courses}
      coursesUserMap={coursesUserMap}
    />
  );
};

const mapStateToProps = state => ({
  academicYearId: getSelectedAcademicYearId(state),
});

const mapActionCreators = {
  setToastMsg,
};

export default connect(
  mapStateToProps,
  mapActionCreators
)(AssignClassSettingsDataGridWrapper);
